import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  Autocomplete,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ContentCopy as ContentCopyIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import {
  CommissionTier,
  CreateCommissionData,
  PROVIDERS,
  ProviderOption,
  useProviders,
  useServiceTypes,
} from '../../hooks/admin/useAdminCommissions';
import { ApiAutocomplete } from '../common';
import ZoneBuilder, { ZoneEntry } from './commission/ZoneBuilder';
import TierEditor from './commission/TierEditor';
import {
  INK,
  FONTS,
  primaryBtnSx,
  ghostBtnSx,
  ledgerInputSx,
  ledgerSelectSx,
  ledgerChipSx,
  ledgerSwitchSx,
} from '../../styles/ledger';

interface User {
  id: number;
  name: string;
  email: string;
  first_name: string;
  last_name: string;
  avatar?: string;
}

interface BulkRow {
  selectedUsers: User[];
  providers: string[];
  service_types: string[];
  origin_zones: ZoneEntry[];
  destination_zones: ZoneEntry[];
  rateMode: 'flat' | 'tiers';
  value_amount: number;
  value_type: 'fixed' | 'percentage';
  tiers: CommissionTier[];
  priority: number;
  is_active: boolean;
  error?: string;
}

const emptyRow = (): BulkRow => ({
  selectedUsers: [],
  providers: [],
  service_types: [],
  origin_zones: [],
  destination_zones: [],
  rateMode: 'flat',
  value_amount: 5,
  value_type: 'percentage',
  tiers: [],
  priority: 1,
  is_active: true,
});

const rowToPayload = (row: BulkRow): CreateCommissionData => ({
  user_ids: row.selectedUsers.length > 0 ? row.selectedUsers.map((u) => u.id) : null,
  providers: row.providers.length > 0 ? row.providers : null,
  service_types: row.service_types.length > 0 ? row.service_types : null,
  origin_zones: row.origin_zones.length > 0 ? row.origin_zones.map(({ type, id }) => ({ type, id })) : null,
  destination_zones:
    row.destination_zones.length > 0 ? row.destination_zones.map(({ type, id }) => ({ type, id })) : null,
  commission_tiers:
    row.rateMode === 'tiers'
      ? row.tiers
      : [
          {
            weight_min: 0,
            weight_max: null,
            value_amount: row.value_amount,
            value_type: row.value_type,
          },
        ],
  priority: row.priority,
  is_active: row.is_active,
});

interface RowEditorProps {
  row: BulkRow;
  index: number;
  providerOptions: ProviderOption[];
  onChange: (index: number, patch: Partial<BulkRow>) => void;
  onClone: (index: number) => void;
  onRemove: (index: number) => void;
  canRemove: boolean;
  disabled: boolean;
}

const RowEditor: React.FC<RowEditorProps> = ({
  row,
  index,
  providerOptions,
  onChange,
  onClone,
  onRemove,
  canRemove,
  disabled,
}) => {
  const { data: serviceTypes = {} } = useServiceTypes(row.providers);
  const serviceOptions = Object.entries(serviceTypes).flatMap(([category, services]) =>
    Object.entries(services).map(([code, name]) => ({ value: code, label: name, category }))
  );

  return (
    <Box
      sx={{
        border: `1px solid ${row.error ? INK.inactive : INK.hairline}`,
        borderRadius: 0.5,
        p: 2,
        bgcolor: INK.surface,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <Typography sx={{ fontFamily: FONTS.mono, fontSize: 11, color: INK.accent, fontWeight: 600 }}>
          {String(index + 1).padStart(2, '0')}
        </Typography>
        <Box sx={{ flex: 1, height: 1, bgcolor: INK.hairlineSoft }} />
        <Tooltip title="Clone row">
          <span>
            <IconButton size="small" disabled={disabled} onClick={() => onClone(index)} sx={{ color: INK.muted }}>
              <ContentCopyIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Remove row">
          <span>
            <IconButton
              size="small"
              disabled={disabled || !canRemove}
              onClick={() => onRemove(index)}
              sx={{ color: INK.muted, '&:hover': { color: INK.inactive } }}
            >
              <DeleteIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      {row.error && (
        <Alert severity="error" sx={{ mb: 1.5, borderRadius: 0, py: 0 }}>
          {row.error}
        </Alert>
      )}

      <Stack spacing={1.5}>
        <ApiAutocomplete<User>
          multiple
          endpoint="admin/users"
          searchParamName="search"
          labelKey={(u) => u.name || [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email}
          valueKey="id"
          secondaryLabelKey="email"
          disabled={disabled}
          value={row.selectedUsers}
          onChange={(users) => onChange(index, { selectedUsers: (users as User[]) || [] })}
          label="Users"
          placeholder="Search by name or email…"
          minSearchLength={2}
          textFieldProps={{
            helperText: 'Empty → general commission for all users',
            size: 'small',
            sx: ledgerInputSx,
          }}
        />

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <Autocomplete
            multiple
            size="small"
            fullWidth
            disabled={disabled}
            options={providerOptions}
            getOptionLabel={(o) => o.label}
            value={providerOptions.filter((p) => row.providers.includes(p.value))}
            onChange={(_, v) => onChange(index, { providers: v.map((x) => x.value), service_types: [] })}
            renderInput={(params) => (
              <TextField {...params} label="Providers" placeholder="All providers" sx={ledgerInputSx} />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, i) => (
                <Chip label={option.label} {...getTagProps({ index: i })} size="small" sx={ledgerChipSx} />
              ))
            }
          />
          <Autocomplete
            multiple
            size="small"
            fullWidth
            disabled={disabled || row.providers.length === 0}
            options={serviceOptions}
            groupBy={(o) => o.category.toUpperCase()}
            getOptionLabel={(o) => o.label}
            value={serviceOptions.filter((s) => row.service_types.includes(s.value))}
            onChange={(_, v) => onChange(index, { service_types: v.map((x) => x.value) })}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Service types"
                placeholder={row.providers.length === 0 ? 'Pick a provider first' : 'All services'}
                sx={ledgerInputSx}
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, i) => (
                <Chip label={option.label} {...getTagProps({ index: i })} size="small" sx={ledgerChipSx} />
              ))
            }
          />
        </Stack>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems="stretch">
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <ZoneBuilder
              direction="origin"
              zones={row.origin_zones}
              onChange={(zones) => onChange(index, { origin_zones: zones })}
              disabled={disabled}
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 28,
              color: INK.faint,
              transform: { xs: 'rotate(90deg)', md: 'none' },
            }}
          >
            <ArrowForwardIcon sx={{ fontSize: 16 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <ZoneBuilder
              direction="destination"
              zones={row.destination_zones}
              onChange={(zones) => onChange(index, { destination_zones: zones })}
              disabled={disabled}
            />
          </Box>
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
          <Select
            size="small"
            disabled={disabled}
            value={row.rateMode}
            onChange={(e) => onChange(index, { rateMode: e.target.value as 'flat' | 'tiers' })}
            sx={{ ...ledgerSelectSx, width: { sm: 150 } }}
          >
            <MenuItem value="flat" sx={{ fontFamily: FONTS.sans, fontSize: 13 }}>Flat rate</MenuItem>
            <MenuItem value="tiers" sx={{ fontFamily: FONTS.sans, fontSize: 13 }}>Weight tiers</MenuItem>
          </Select>
          {row.rateMode === 'flat' && (
            <>
              <TextField
                type="number"
                size="small"
                label="Value"
                disabled={disabled}
                value={row.value_amount}
                onChange={(e) => onChange(index, { value_amount: parseFloat(e.target.value) || 0 })}
                InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                sx={{ ...ledgerInputSx, width: { sm: 120 } }}
              />
              <Select
                size="small"
                disabled={disabled}
                value={row.value_type}
                onChange={(e) => onChange(index, { value_type: e.target.value as 'fixed' | 'percentage' })}
                sx={{ ...ledgerSelectSx, width: { sm: 140 } }}
              >
                <MenuItem value="percentage" sx={{ fontFamily: FONTS.sans, fontSize: 13 }}>Percent</MenuItem>
                <MenuItem value="fixed" sx={{ fontFamily: FONTS.sans, fontSize: 13 }}>ILS</MenuItem>
              </Select>
            </>
          )}
          <TextField
            type="number"
            size="small"
            label="Priority"
            disabled={disabled}
            value={row.priority}
            onChange={(e) => onChange(index, { priority: parseInt(e.target.value) || 1 })}
            InputProps={{ inputProps: { min: 1 } }}
            sx={{ ...ledgerInputSx, width: { sm: 110 } }}
          />
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ ml: { sm: 'auto' } }}>
            <Switch
              size="small"
              disabled={disabled}
              checked={row.is_active}
              onChange={(e) => onChange(index, { is_active: e.target.checked })}
              sx={ledgerSwitchSx}
            />
            <Typography sx={{ fontFamily: FONTS.mono, fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.1em', color: row.is_active ? INK.active : INK.inactive }}>
              {row.is_active ? 'Active' : 'Paused'}
            </Typography>
          </Stack>
        </Stack>

        {row.rateMode === 'tiers' && (
          <Box>
            <TierEditor
              tiers={row.tiers}
              onChange={(tiers) => onChange(index, { tiers })}
              disabled={disabled}
            />
          </Box>
        )}
      </Stack>
    </Box>
  );
};

interface BulkCommissionAddProps {
  createCommission: (data: CreateCommissionData) => Promise<any>;
  onCompleted: (created: number, failed: number) => void;
}

const BulkCommissionAdd: React.FC<BulkCommissionAddProps> = ({ createCommission, onCompleted }) => {
  const [rows, setRows] = useState<BulkRow[]>([emptyRow()]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { data: providerOptions = PROVIDERS } = useProviders();

  const patchRow = (index: number, patch: Partial<BulkRow>) =>
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch, error: undefined } : r)));

  const cloneRow = (index: number) =>
    setRows((prev) => [...prev.slice(0, index + 1), { ...prev[index], error: undefined }, ...prev.slice(index + 1)]);

  const removeRow = (index: number) => setRows((prev) => prev.filter((_, i) => i !== index));

  const handleSubmit = async () => {
    // Client-side check before firing any request: tier mode needs tiers
    const invalid = rows.some((r) => r.rateMode === 'tiers' && r.tiers.length === 0);
    if (invalid) {
      setRows((prev) =>
        prev.map((r) =>
          r.rateMode === 'tiers' && r.tiers.length === 0
            ? { ...r, error: 'Add at least one weight tier, or switch to a flat rate.' }
            : r
        )
      );
      return;
    }

    setIsSubmitting(true);
    const results = await Promise.allSettled(rows.map((row) => createCommission(rowToPayload(row))));
    const failedRows: BulkRow[] = [];
    let created = 0;
    results.forEach((result, i) => {
      if (result.status === 'fulfilled') {
        created += 1;
      } else {
        const reason: any = result.reason;
        const apiErrors = reason?.response?.data?.errors;
        const message = apiErrors
          ? Object.values(apiErrors).flat().join(' ')
          : reason?.message || 'Failed to create';
        failedRows.push({ ...rows[i], error: message });
      }
    });
    setIsSubmitting(false);
    setRows(failedRows.length > 0 ? failedRows : [emptyRow()]);
    onCompleted(created, failedRows.length);
  };

  return (
    <Stack spacing={2}>
      <Typography sx={{ fontFamily: FONTS.sans, fontSize: 12.5, color: INK.muted, lineHeight: 1.5 }}>
        Each row becomes one commission — same options as “New commission”: users, providers,
        services, corridors, and either a flat rate or weight tiers.
      </Typography>

      {rows.map((row, index) => (
        <RowEditor
          key={index}
          row={row}
          index={index}
          providerOptions={providerOptions}
          onChange={patchRow}
          onClone={cloneRow}
          onRemove={removeRow}
          canRemove={rows.length > 1}
          disabled={isSubmitting}
        />
      ))}

      <Button
        onClick={() => setRows((prev) => [...prev, emptyRow()])}
        startIcon={<AddIcon sx={{ fontSize: 16 }} />}
        disabled={isSubmitting}
        fullWidth
        sx={{ ...ghostBtnSx, py: 1.1, borderStyle: 'dashed', color: INK.muted }}
      >
        Add row
      </Button>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          onClick={handleSubmit}
          disabled={isSubmitting || rows.length === 0}
          sx={{ ...primaryBtnSx, px: 3 }}
          startIcon={isSubmitting ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : null}
        >
          {isSubmitting ? 'Creating…' : `Create ${rows.length} ${rows.length === 1 ? 'commission' : 'commissions'}`}
        </Button>
      </Box>
    </Stack>
  );
};

export default BulkCommissionAdd;
